import React, { useState, useEffect } from 'react';
import { sans, mono, green, red, useTheme } from '../../tokens';
import { getOwnershipSummary, subscribeToCapTableChanges } from '../../data/capTableSource';

// Styled to mirror the Capital Structure card on its left: same header,
// same big green headline value, same per-series row breakdown, same
// clickable highlight pattern. Palette is derived from the brand green
// so it harmonizes with the KPI cards above instead of clashing.

const SERIES_COLORS = ['#1A9A6C', '#4CB38A', '#7FCBA8', '#B2E3C6', '#E5F4EC'];

export default function OwnershipCard() {
  const t = useTheme();
  const [data, setData] = useState(() => getOwnershipSummary());
  const [selected, setSelected] = useState(null);

  // Re-read whenever CapTable saves edits.
  useEffect(() => {
    return subscribeToCapTableChanges(() => setData(getOwnershipSummary()));
  }, []);

  const totalM = data.fullyDiluted / 1000000;
  const sel = selected !== null ? data.breakdown[selected] : null;
  const headlineValue = sel
    ? `${(sel.shares / 1000000).toFixed(2)}M`
    : `${totalM.toFixed(2)}M`;
  const headlineLabel = sel ? sel.label : 'All Shareholders';

  return (
    <div style={{
      background: t.bgCard,
      border: `1px solid ${t.border}`,
      borderRadius: 10,
      padding: '20px 24px',
    }}>
      {/* Header — matches Capital Structure */}
      <div style={{ marginBottom: 14 }}>
        <div style={{
          fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: 0.5, color: t.textMuted, fontFamily: sans,
        }}>
          Ownership Summary
        </div>
      </div>

      {/* Headline value — mirrors "Remaining $38.8M" styling */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: 0.4, color: t.textMuted, fontFamily: sans, marginBottom: 2 }}>
          Fully Diluted
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: t.textPrimary }}>
          {headlineValue} <span style={{ fontSize: 11, color: t.textMuted, fontWeight: 500 }}>shares</span>
        </div>
      </div>

      {/* Breakdown bar — mirrors Capital Structure's deployment bar */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <span style={{ fontSize: 10, fontFamily: mono, color: t.textSecondary }}>
            {headlineLabel}
          </span>
          <span style={{ fontSize: 11, fontWeight: 600, fontFamily: mono, color: t.textPrimary }}>
            {sel ? `${sel.pct.toFixed(1)}%` : '100%'}
          </span>
        </div>
        <div style={{ height: 16, borderRadius: 4, overflow: 'hidden', background: t.borderLight, display: 'flex' }}>
          {selected === null ? data.breakdown.map((b, i) => (
            <div key={i} style={{
              height: '100%', width: `${b.pct}%`,
              background: SERIES_COLORS[i % SERIES_COLORS.length],
              transition: 'width 0.3s',
            }} />
          )) : (
            <div style={{
              height: '100%', width: `${sel.pct}%`,
              background: SERIES_COLORS[selected % SERIES_COLORS.length],
              borderRadius: 4, transition: 'width 0.3s',
            }} />
          )}
        </div>
      </div>

      {/* Per-series rows — mirrors the Capital Structure breakdown list */}
      <div style={{ borderTop: `1px solid ${t.borderLight}`, paddingTop: 6 }}>
        {data.breakdown.map((b, i) => {
          const isSelected = selected === i;
          return (
            <div key={b.label}
              onClick={() => setSelected(isSelected ? null : i)}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '4px 6px', margin: '1px 0', borderRadius: 4, cursor: 'pointer',
                background: isSelected ? (SERIES_COLORS[i % SERIES_COLORS.length] + '18') : 'transparent',
                transition: 'background 0.15s',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 7, height: 7, borderRadius: 2, background: SERIES_COLORS[i % SERIES_COLORS.length], flexShrink: 0 }} />
                <span style={{
                  fontSize: 11, fontFamily: sans,
                  color: isSelected ? t.textPrimary : t.textSecondary,
                  fontWeight: isSelected ? 600 : 400,
                }}>
                  {b.label}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, fontFamily: mono, color: t.textSecondary }}>
                  {b.pct.toFixed(1)}%
                </span>
                <span style={{ fontSize: 8, fontFamily: mono, color: t.textFaint, marginLeft: 4 }}>
                  {(b.shares / 1000000).toFixed(2)}M
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Canadian ownership footer */}
      <div style={{
        marginTop: 10, paddingTop: 8,
        borderTop: `1px solid ${t.borderLight}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: 10, fontFamily: sans, color: t.textMuted }}>
          Canadian Ownership
        </span>
        <span style={{
          fontSize: 11, fontWeight: 700, fontFamily: mono,
          color: data.canadianPct >= 50 ? green : red,
        }}>
          {data.canadianPct.toFixed(1)}%
        </span>
      </div>
    </div>
  );
}
